import { useMemo } from "react";

const BUCKET_TONE = {
  good: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  needs_improvement: "bg-amber-50 text-amber-700 ring-amber-200",
  poor: "bg-red-50 text-red-700 ring-red-200",
  unknown: "bg-gray-50 text-gray-500 ring-gray-200",
};

const METRICS = [
  { key: "ttfb_ms", label: "TTFB", unit: "ms" },
  { key: "fcp_ms", label: "FCP", unit: "ms" },
  { key: "lcp_ms", label: "LCP", unit: "ms" },
  { key: "cls", label: "CLS", unit: "" },
  { key: "inp_ms", label: "INP", unit: "ms" },
  { key: "load_ms", label: "Load", unit: "ms" },
];

const IMPACT_TONE = {
  high: "bg-red-100 text-red-700 ring-red-200",
  medium: "bg-amber-100 text-amber-700 ring-amber-200",
  low: "bg-gray-100 text-gray-600 ring-gray-200",
};

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtValue(metric, value) {
  if (value == null) return "—";
  if (metric === "cls") return value.toFixed(3);
  return `${value} ms`;
}

function MetricCard({ metric, label, unit, value, bucketLabel }) {
  const tone = BUCKET_TONE[bucketLabel] ?? BUCKET_TONE.unknown;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold text-gray-900">
        {fmtValue(metric, value)}
      </p>
      <span
        className={`mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${tone}`}
      >
        {bucketLabel?.replace("_", " ") ?? "unknown"}
      </span>
    </div>
  );
}

function shortUrl(url, max = 50) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.host;
    const path = u.pathname + u.search;
    if (host.length + path.length <= max) return host + path;
    return host + path.slice(0, Math.max(8, max - host.length - 1)) + "…";
  } catch {
    return url.length > max ? url.slice(0, max - 1) + "…" : url;
  }
}

function deriveDelta(current, previous) {
  if (!previous || !current) return [];
  const deltas = [];
  for (const m of METRICS) {
    const a = previous[m.key];
    const b = current[m.key];
    if (a == null || b == null || a === 0) continue;
    const pct = ((b - a) / a) * 100;
    if (Math.abs(pct) < 10) continue;
    deltas.push({
      label: m.label,
      before: a,
      after: b,
      pct: Math.round(pct * 10) / 10,
      metric: m.key,
      direction: b > a ? "worse" : "better",
    });
  }
  for (const key of ["total_bytes", "total_requests"]) {
    const a = previous[key];
    const b = current[key];
    if (!a || !b) continue;
    const pct = ((b - a) / a) * 100;
    if (Math.abs(pct) < 15) continue;
    deltas.push({
      label: key === "total_bytes" ? "Total bytes" : "Total requests",
      before: a,
      after: b,
      pct: Math.round(pct * 10) / 10,
      metric: key,
      direction: b > a ? "worse" : "better",
    });
  }
  return deltas;
}

export default function PageSpeedPanel({ monitor, latestCheckResult, previousGoodCheckResult }) {
  const data = latestCheckResult?.data || {};
  const score = data.score;
  const buckets = data.score_buckets || {};
  const deltas = useMemo(
    () => deriveDelta(data, previousGoodCheckResult?.data),
    [data, previousGoodCheckResult],
  );

  if (!latestCheckResult) {
    return (
      <p className="text-sm text-gray-500">
        Waiting for the first page-speed run.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-teal-500 text-2xl font-bold text-teal-700">
          {score ?? "—"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Performance score
          </p>
          <p className="text-sm text-gray-700">
            {data.title || monitor.target}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {data.total_requests ?? 0} requests •{" "}
            {fmtBytes(data.total_bytes)} •{" "}
            {data.failed_requests ?? 0} failed
          </p>
        </div>
        {data.har_key && (
          <a
            href={`/api/v1/artifacts?key=${encodeURIComponent(data.har_key)}`}
            className="text-sm font-medium text-teal-700 hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            View HAR →
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {METRICS.map((m) => (
          <MetricCard
            key={m.key}
            metric={m.key}
            label={m.label}
            unit={m.unit}
            value={data[m.key]}
            bucketLabel={buckets[m.key]}
          />
        ))}
      </div>

      {deltas.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            What changed since the last good run
          </p>
          <ul className="space-y-1 text-sm">
            {deltas.map((d, i) => (
              <li key={i} className="flex items-center gap-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ${
                    d.direction === "worse"
                      ? "bg-red-50 text-red-700 ring-red-200"
                      : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  }`}
                >
                  {d.direction === "worse" ? "▲" : "▼"} {Math.abs(d.pct)}%
                </span>
                <span className="font-medium text-gray-700">{d.label}</span>
                <span className="text-gray-500">
                  {d.metric === "total_bytes"
                    ? `${fmtBytes(d.before)} → ${fmtBytes(d.after)}`
                    : `${d.before} → ${d.after}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Bytes by type
          </p>
          {Object.entries(data.bytes_by_type || {}).length === 0 ? (
            <p className="text-sm text-gray-400">No HAR captured.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {Object.entries(data.bytes_by_type || {})
                .sort((a, b) => b[1] - a[1])
                .map(([type, bytes]) => (
                  <li key={type} className="flex justify-between">
                    <span className="text-gray-700">{type}</span>
                    <span className="font-mono text-gray-500">
                      {fmtBytes(bytes)} ·{" "}
                      {data.requests_by_type?.[type] ?? 0} reqs
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Render-blocking
          </p>
          {(!data.render_blocking || data.render_blocking.length === 0) ? (
            <p className="text-sm text-gray-400">
              No render-blocking resources detected.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.render_blocking.map((r, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-gray-200">
                    {r.type}
                  </span>
                  <span
                    className="truncate font-mono text-xs text-gray-600"
                    title={r.url}
                  >
                    {shortUrl(r.url)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Top slow resources
        </p>
        {(!data.slowest_resources || data.slowest_resources.length === 0) ? (
          <p className="text-sm text-gray-400">No HAR captured.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.slowest_resources.slice(0, 8).map((r, i) => (
                <tr key={i} className="border-t border-gray-100 first:border-t-0">
                  <td className="py-1.5 pr-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-gray-200">
                      {r.type}
                    </span>
                  </td>
                  <td
                    className="py-1.5 pr-2 font-mono text-xs text-gray-700"
                    title={r.url}
                  >
                    <div className="truncate">{shortUrl(r.url, 60)}</div>
                    {r.initiator && (
                      <div
                        className="truncate text-[10px] text-gray-400"
                        title={`Loaded by: ${r.initiator}`}
                      >
                        ← {shortUrl(r.initiator, 60)}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-1.5 pr-2 text-right text-xs text-gray-500">
                    {Math.round(r.time_ms)} ms
                  </td>
                  <td className="whitespace-nowrap py-1.5 text-right text-xs text-gray-500">
                    {fmtBytes(r.size_bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data.third_party_impact || []).length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Third-party impact
          </p>
          <ul className="space-y-1 text-sm">
            {data.third_party_impact.map((tp, i) => (
              <li key={i} className="flex items-center gap-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ${
                    IMPACT_TONE[tp.impact] ?? IMPACT_TONE.low
                  }`}
                >
                  {tp.impact}
                </span>
                <span className="font-medium text-gray-700">{tp.provider}</span>
                <span className="text-xs text-gray-500">
                  {tp.request_count} reqs · {Math.round(tp.total_duration_ms)} ms
                  {tp.render_blocking && " · render-blocking"}
                  {tp.failed_requests > 0 && ` · ${tp.failed_requests} failed`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
